import { createAppSlice } from "../../app/createAppSlice";
import type { AppThunk } from "../../app/store";
import { ng } from "@common/ngds";
import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { useAuth0 } from "@auth0/auth0-react";
import { fetchProject, ProjectSlice } from "../projects/projectSlice";
import { getClientId } from "@common/helpers/funlib";
import { AlertMessage } from "../../helpers/AlertMessage";
import { useEffect } from "react";
import { openModal, UISlice } from "../ui/uiSlice";
import { useTranslation } from "react-i18next";

export const getUser = createAction<number>("api/users");

const parseJsonSafely = async (resp: Response) => {
  try {
    return await resp.json();
  } catch {
    return null;
  }
};

export const fetchUser = createAsyncThunk(
  "users/user",
  async (item, thunkAPI) => {
    const state: any = thunkAPI.getState();
    const token = state?.UserSlice?.token;
    if (!token) {
    }
    const tempheaders = {
      authorization: `Bearer ${token}`,
      "x-client-id": getClientId(),
    };

    const resp = await fetch(`/api/users/`, { headers: tempheaders });
    if (!resp.ok) {
      console.error("Failed to fetch user", resp.status, resp.statusText);
      //state?.UserSlice?.setLoadingMessage(resp.statusText);
      const errorResponse = await parseJsonSafely(resp);
      console.log("Failed to fetch user response:", errorResponse);
      if (errorResponse) return errorResponse?.statusText;
      else {
        return resp.statusText;
      }
    }
    const a = await parseJsonSafely(resp);

    //
    if (Object.keys(a.settings).length == 0) {
      a.settings = { lang: "en", showDisclaimer: true, autoZoomViewportEnabled: false, analyseOnSave: false };
    }

    return a ?? null;
  },
  {
    condition: (_args, { getState }) => {
      const state = getState() as { UserSlice?: { status?: string } };
      return state.UserSlice?.status !== "loading";
    },
  },
);

export const updateUserSettings = createAsyncThunk(
  "users/usersettings",
  async (user, thunkAPI) => {
    const state: any = thunkAPI.getState();
    const token = state?.UserSlice?.token;
    if (!token) {
      console.error("No token found in Exist");
      return null;
    }
    const tempheaders = {
      authorization: `Bearer ${token}`,
      "x-client-id": getClientId(),
      "Content-Type": "application/json",
    };

    try {
      const resp = await fetch(`/api/users/`, { method: "PUT", headers: tempheaders, body: JSON.stringify(user) });
      const a = await parseJsonSafely(resp);

      if (!resp.ok) {
        AlertMessage("error_", a?.statusCode, "", a?.statusText || "Unknown error");

        return a?.statusText || "Unknown error";
      }

      return a ?? null;
    } catch (error) {
      console.error("Failed to fetch user", error);
      return null;
    }
  },
  {
    condition: (_args, { getState }) => {
      const state = getState() as { UserSlice?: { status?: string } };
      return state.UserSlice?.status !== "loading";
    },
  },
);

export const deletUser = createAsyncThunk("users/deleteUser", async (item, thunkAPI) => {
  const state: any = thunkAPI.getState();
  const token = state?.UserSlice?.token;
  if (!token) {
    console.error("No token found when deleting user");
    return null;
  }

  const tempheaders = {
    "x-client-id": getClientId(),
    authorization: `Bearer ${token}`,
  };

  try {
    const resp = await fetch(`/api/users/delete`, { headers: tempheaders, method: "POST" });
    const a = await parseJsonSafely(resp);

    if (!resp.ok) {
      AlertMessage("error_", a?.statusCode, "", a?.statusText || "Unknown error");
      return null;
    }

    return a ?? null;
  } catch (error) {
    console.error("Failed to delete user", error);
    return null;
  }
});

export const releaseLicenceUser = createAsyncThunk("users/releaseLicenceUser", async (item, thunkAPI) => {
  const state: any = thunkAPI.getState();
  const token = state?.UserSlice?.token;
  if (!token) {
    console.error("No token found in Exist");
    return null;
  }

  const tempheaders = {
    "x-client-id": getClientId(),
    authorization: `Bearer ${token}`,
  };

  try {
    const resp = await fetch(`/api/users/logout`, { headers: tempheaders });
    const a = await parseJsonSafely(resp);

    if (!resp.ok) {
      AlertMessage("error_", a?.statusCode, "", a?.statusText || "Unknown error");
      return null;
    }

    return a ?? null;
  } catch (error) {
    console.error("Failed to release user licence", error);
    return null;
  }
});

export const getTokenFromAuth0 = createAsyncThunk(
  "users/getTokenFromAuth0",
  async (args: { getAccessToken: any; cache: boolean }) => {
    if (!args?.getAccessToken) {
      console.error("Missing getAccessToken function");
      return null;
    }

    try {
      const accessToken = await args.getAccessToken({
        cacheMode: args.cache ? "on" : "off",
        authorizationParams: {
          scope: "openid profile email read:current_user",
          ignoreCache: args.cache == false,
        },
      });
      return accessToken ?? null;
    } catch (e) {
      console.error("Error getting access token:", e);
      return null;
    }
  },
);

export function useUser() {
  const dispatch = useAppDispatch();
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
  const state = useAppSelector((state) => state);
  const { t, i18n } = useTranslation();
  let user = useAppSelector(selectUser);

  useEffect(() => {
    let a = async () => {
      if (isAuthenticated && !isLoading && state.UserSlice?.status !== "loading") {
        console.log("Fetching user data...", isLoading, isAuthenticated, state.UserSlice?.status);
        if (!state.UserSlice?.user) await getUser();
      }
    };

    a();
  }, [isAuthenticated, isLoading]);

  const getUser = async () => {
    try {
      if (!isLoading && isAuthenticated) {
        const userAction = await dispatch(fetchUser());

        if (!fetchUser.fulfilled.match(userAction) || !userAction.payload || typeof userAction.payload == "string") {
          if ((userAction as any).error && (userAction as any).error.name == "ConditionError") {
            return;
          }
          dispatch(UISlice.actions.toggleDisableUser(true));
          dispatch(
            UISlice.actions.setLoadingMessage(
              "Failed to fetch user data. Please try again later., Following error:" + userAction.payload,
            ),
          );

          console.error("Failed to fetch user or user payload is empty", userAction);
          return;
        }

        const payload = userAction.payload;

        if (payload.tempFile && !window.location.pathname.startsWith("/files/")) {
          const tempFileGuid = payload.tempFile.guid;
          if (typeof tempFileGuid === "string" && tempFileGuid.length > 0) {
            await dispatch(fetchProject({ guid: tempFileGuid }));
          } else {
            dispatch(ProjectSlice.actions.setProject(payload.tempFile));
          }
        }
        const langCode = payload?.settings?.lang || i18n.resolvedLanguage || i18n.language || "en";
        if (langCode !== i18n.language) {
          await i18n.changeLanguage(langCode);
        }
        if (typeof payload === "string") {
          dispatch(UISlice.actions.toggleDisableUser(true));
          dispatch(UISlice.actions.setLoadingMessage("Failed to fetch user data. Please try again later.," + payload));
          //console.error("Failed to fetch user or user payload is empty, ", payload);
          setUser(null);
          return;
        }
        // dispatch(fetchSections({}));
        //   dispatch(fetchMaterials({}));
        dispatch(UISlice.actions.toggleDisableUser(false));
        setUser(payload);
        if (payload.settings?.showDisclaimer) {
          dispatch(openModal("disclaimerModal"));
        }
      }
    } catch (error) {
      console.error("Unexpected error while getting user", error);
    }
  };

  const hideDisclaimerModal = async (showDisclaimer: boolean) => {
    const updatedUser = {
      ...user,
      settings: { ...user.settings, showDisclaimer },
    };

    await dispatch(UserSlice.actions.setUser(updatedUser)); // update Redux store

    await updateSettingsForUser(updatedUser);
  };

  const saveSurveyData = async (surveyid: string) => {
    let newuser = state.UserSlice.user;

    let s = structuredClone(newuser);

    s.settings = { ...newuser.settings, completedSurvey: surveyid };

    await dispatch(UserSlice.actions.setUser(s)); // update Redux store

    await updateSettingsForUser(s);
  };

  const scheduleSurveyPrompt = async (surveyPromptAt: number) => {
    const newuser = state.UserSlice.user;
    if (!newuser || newuser.settings?.surveyPromptAt || newuser.settings?.completedSurvey) {
      return;
    }

    const s = structuredClone(newuser);

    s.settings = { ...newuser.settings, surveyPromptAt };

    await dispatch(UserSlice.actions.setUser(s)); // update Redux store

    await updateSettingsForUser(s);
  };
  const resetscheduleSurveyPrompt = async (surveyPromptAt: number) => {
    const newuser = state.UserSlice.user;
    if (!newuser || !newuser.settings?.surveyPromptAt) {
      return;
    }

    const s = structuredClone(newuser);

    delete s.settings.surveyPromptAt;
    s.settings = { ...s.settings };

    await dispatch(UserSlice.actions.setUser(s)); // update Redux store

    await updateSettingsForUser(s);
  };

  const setUser = (user: any) => {
    if (!user) {
      dispatch(UserSlice.actions.clearUser());
      dispatch(UISlice.actions.toggleDisableUser(true));
      return;
    }

    dispatch(UserSlice.actions.setUser(user));
  };

  const getToken = async (cache: boolean = true) => {
    try {
      const tokenAction = await dispatch(getTokenFromAuth0({ getAccessToken: getAccessTokenSilently, cache }));

      if (getTokenFromAuth0.rejected.match(tokenAction) || !tokenAction.payload) {
        console.error("Failed to get token", tokenAction);
      }
    } catch (error) {
      console.error("Unexpected error while getting token", error);
    }
  };

  const getRolesFromToken = async () => {
    try {
      // Implement logic to extract roles from the token
      const token = selectToken(state);

      if (!token) {
        console.error("No token found when getting roles");
        return [];
      }
      const base64Payload = token.split(".")[1];
      const json = atob(base64Payload.replace(/-/g, "+").replace(/_/g, "/"));
      const decoded = JSON.parse(json);

      const roles = decoded["https://portal.onstrusoft.com/roles"] ?? []; //parseRolesFromToken(token); // Replace with actual implementation
      return roles;
    } catch (error) {
      console.error("Unexpected error while getting roles from token", error);
      return [];
    }
  };
  const deleteUser = async () => {
    try {
      const deleteAction = await dispatch(deletUser());
      if (deletUser.rejected.match(deleteAction)) {
        console.error("Failed to delete user", deleteAction);
      }
    } catch (error) {
      console.error("Unexpected error while deleting user", error);
    }
  };

  const updateSettingsForUser = async (updatedUser?: typeof state.UserSlice.user) => {
    try {
      const userToSave = updatedUser ?? state.UserSlice?.user;
      const updateAction = await dispatch(updateUserSettings(userToSave));
      if (updateUserSettings.rejected.match(updateAction)) {
        console.error("Failed to update user", updateAction);
      }
    } catch (error) {
      console.error("Unexpected error while updating user", error);
    }
  };

  const logOut = async () => {
    try {
      const logoutAction = await dispatch(releaseLicenceUser());
      if (releaseLicenceUser.rejected.match(logoutAction)) {
        console.error("Failed to release licence on logout", logoutAction);
      }
    } catch (error) {
      console.error("Unexpected error while logging out", error);
    }
    dispatch(UserSlice.actions.clearUser());
  };
  return {
    getUser,
    getToken,
    logOut,
    deleteUser,
    getRolesFromToken,
    updateSettingsForUser,
    hideDisclaimerModal,
    saveSurveyData,
    scheduleSurveyPrompt,
    resetscheduleSurveyPrompt,
  };
}

export type userState = {
  user: any | null;
  token: string | null;
  status: "idle" | "loading" | "failed";
};

const initialState: userState = {
  user: null,
  status: "idle",
  token: null,
};

export const UserSlice = createAppSlice({
  name: "UserSlice",
  initialState: initialState,
  reducers: {
    setUser: (state, action) => {
      state.user = action.payload;
    },
    clearUser: (state) => {
      state.user = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUser.pending, (state) => {
      state.status = "loading";
      // console.log(" user pending");
    });

    builder.addCase(fetchUser.fulfilled, (state, action) => {
      state.status = "idle";

      if (typeof action.payload != "string") {
        state.user = action.payload ?? null;
      } else {
        state.user = null;
      }
    });

    builder.addCase(fetchUser.rejected, (state) => {
      state.status = "failed";
      state.user = null;
    });

    builder.addCase(updateUserSettings.pending, (state) => {
      state.status = "loading";
      // console.log(" user pending");
    });

    builder.addCase(updateUserSettings.fulfilled, (state, action) => {
      state.status = "idle";
    });

    builder.addCase(updateUserSettings.rejected, (state) => {
      state.status = "failed";
    });

    builder.addCase(getTokenFromAuth0.pending, () => {
      console.log(" get token pending");
    });

    builder.addCase(getTokenFromAuth0.fulfilled, (state, action: any) => {
      state.token = action.payload ?? null;
    });

    builder.addCase(getTokenFromAuth0.rejected, (state) => {
      state.token = null;
    });
  },
  selectors: {
    selectUser: (state) => state.user,
    selectToken: (state) => state.token,
  },
});

export const { selectUser, selectToken } = UserSlice.selectors;

/**
 * Whether the viewports should keep zooming to fit by themselves, rather than letting the user
 * pan and zoom. Defaults to true so a user whose settings predate the option (or who has not
 * loaded yet) keeps the original behaviour.
 */
export function useAutoZoomViewport(): boolean {
  const user = useAppSelector(selectUser);
  return user?.settings?.autoZoomViewportEnabled ?? true;
}
